import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { TinyAd } from '@/components/TinyAd';
import { 
  Clock, MapPin, TrendingUp, Tv, Youtube, Play, Shield, 
  BarChart2, AlertTriangle 
} from 'lucide-react';
import mpegts from 'mpegts.js'; 

import { BannerAd } from '@/components/BannerAd';
import fallbackBadge from './logo.png';

const BroadcasterButton = ({ name, icon: Icon, url, color = "bg-gray-800" }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className={`flex items-center justify-center gap-2 px-5 py-3 rounded-lg 
                ${color} hover:bg-gray-700 transition-colors text-white font-medium
                border border-gray-700 hover:border-gray-500`}
  >
    <Icon className="w-5 h-5" />
    {name}
  </a>
);

const StatBar = ({ label, home, away, colorHome = "bg-emerald-600", colorAway = "bg-blue-600" }) => {
  const total = (home || 0) + (away || 0) || 1;
  const homePercent = Math.round((home / total) * 100);
  const awayPercent = 100 - homePercent;

  return (
    <div className="mb-5">
      <div className="flex justify-between text-sm mb-2">
        <span className="font-medium text-white">{home || 0}</span>
        <span className="text-gray-400">{label}</span>
        <span className="font-medium text-white">{away || 0}</span>
      </div>
      <div className="h-2.5 bg-gray-800 rounded-full overflow-hidden flex">
        <div 
          className={`${colorHome} transition-all duration-1000`} 
          style={{ width: `${homePercent}%` }}
        />
        <div 
          className={`${colorAway} transition-all duration-1000`} 
          style={{ width: `${awayPercent}%` }}
        />
      </div>
    </div>
  );
};

const streamSources = [
  {
    title: 'HD',
    type: 'iframe',
    src: 'https://www.youtube.com/embed/vJZQPEKZsKQ?autoplay=1'
  },
  {
    title: 'Stable',
    type: 'iframe',
    src: 'https://pl.kooralive.fit/albaplayer/ch1/?serv=1'
  },
  {
    title: 'FHD',
    type: 'iframe',
    src: 'https://www.youtube.com/embed/5HfdECys0UY'
  },
  // {
  //   title: 'TAMANYAH 4K (Proxied)',
  //   type: 'mpegts',
  //   src: `https://ingamefut-backend.vercel.app/api/stream?url=${encodeURIComponent('http://maveniptv.net:80/ahmedELmetwally85/ah8759946/283470')}`
  // },
  // {
  //   title: 'TAMANYAH HD (Proxied)',
  //   type: 'mpegts',
  //   src: `https://ingamefut-backend.vercel.app/api/stream?url=${encodeURIComponent('http://maveniptv.net:80/ahmedELmetwally85/ah8759946/283461')}`
  // },
  // {
  //   title: 'TAMANYAH SD (Proxied)',
  //   type: 'mpegts',
  //   src: `https://ingamefut-backend.vercel.app/api/stream?url=${encodeURIComponent('http://maveniptv.net:80/ahmedELmetwally85/ah8759946/283452')}`
  // },
  // Add more qualities if needed (LOW/SD are usually more stable)
];

const KooralivePlayer = () => {
  return (
    <iframe
      src="https://pl.kooralive.fit/albaplayer/ch1/?serv=1"
      width="100%"
      height="620"
      frameBorder="0"
      allow="autoplay; encrypted-media"
      allowFullScreen
      title="Kooralive Stream"
    />
  );
};

const OkVideoPlayer = ({ videoId, width = 640, height = 360 }) => {
  const src = `https://ok.ru/videoembed/${videoId}`;
  return (
    <iframe
      src={src}
      width={width}
      height={height}
      frameBorder="0"
      allowFullScreen
      title="OK.ru Video"
    />
  );
};

export const MatchPage = () => {
  const { id } = useParams();
  const [match, setMatch] = useState(null);
  const [stats, setStats] = useState(null);
  const [penaltyInfo, setPenaltyInfo] = useState(null); // { homePens, awayPens, winner }
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [streamStatus, setStreamStatus] = useState('idle'); // idle | loading | playing | buffering | error | needs-interaction

  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const isLiveOrUpcoming = match?.status === 'live' || match?.status === 'upcoming';

  // Fetch match info
  useEffect(() => {
    const fetchMatch = async () => {
      if (!id) { setError(true); setLoading(false); return; }

      try {
        const res = await fetch(`https://ingamefut-backend.vercel.app/api/match/${id}`);
        if (!res.ok) throw new Error('Match not found');
        const json = await res.json();

        const home = json.header?.teams?.[0] || json.home || {};
        const away = json.header?.teams?.[1] || json.away || {};
        const status = json.header?.status || json.status || {};
        const stadiumInfo = json.content?.infoBox?.Stadium || 
                    json.seo?.eventJSONLD?.location || 
                    {};

        const mapped = {
          id: parseInt(id),
          group: json.header?.tournamentInfo?.leagueName || json.tournamentStage || 'Knockout Stage',
          homeTeam: home.name || home.shortName || 'Home Team',
          awayTeam: away.name || away.shortName || 'Away Team',
          homeBadge: home.id ? `https://images.fotmob.com/image_resources/logo/teamlogo/${home.id}.png` : 'https://via.placeholder.com/160',
          awayBadge: away.id ? `https://images.fotmob.com/image_resources/logo/teamlogo/${away.id}.png` : 'https://via.placeholder.com/160',
          homeScore: json.header?.teams?.[0]?.score ?? json.homeScore ?? null,
          awayScore: json.header?.teams?.[1]?.score ?? json.awayScore ?? null,
          kickoffTime: status.utcTime || json.matchDate || new Date().toISOString(),
          stadium: stadiumInfo.name || json.venue?.name || json.header?.venue?.name || 'Venue TBA',
          city: stadiumInfo.city
            ? `${stadiumInfo.city}${stadiumInfo.country ? ', ' + stadiumInfo.country : ''}`
            : (stadiumInfo.address?.addressLocality
                ? `${stadiumInfo.address.addressLocality}${stadiumInfo.address?.addressCountry ? ', ' + stadiumInfo.address.addressCountry : ''}`
                : 'Location TBA'),
          status: status.finished ? 'finished' : (status.live ? 'live' : 'upcoming'),
          minute: json.header?.status?.liveTime?.short?.replace(/‎/g, '') || null,
        };
        // Penalty info
        if (json.header?.teams?.[0]?.penalties !== undefined && 
                json.header?.teams?.[1]?.penalties !== undefined) {
              
              const homePens = json.header.teams[0].penalties;
              const awayPens = json.header.teams[1].penalties;
              
              setPenaltyInfo({
                homePens,
                awayPens,
                winner: homePens > awayPens ? mapped.homeTeam :
                        awayPens > homePens ? mapped.awayTeam : 'Draw'
              });
            }
        // After creating mapped
        if (json.header?.status?.reason?.penalties?.length === 2) {
          const [homePens, awayPens] = json.header.status.reason.penalties;
          setPenaltyInfo({
            homePens,
            awayPens,
            winner: homePens > awayPens ? mapped.homeTeam : awayPens > homePens ? mapped.awayTeam : 'Draw',
            aggregate: json.header.status.reason.long || null  // "Pen 2 - 4"
          });
        } else if (json.header?.status?.reason?.long?.includes('Pen')) {
          setPenaltyInfo({
            aggregate: json.header.status.reason.long,
            homePens: null,
            awayPens: null,
            winner: null
          });
        }
            
        setMatch(mapped);

        const allStats = json.content?.stats?.Periods?.All?.stats || json.stats?.stats || [];

        const findStatGroup = (possibleGroupNames) => {
          const names = Array.isArray(possibleGroupNames) ? possibleGroupNames : [possibleGroupNames];
          return allStats.find(group => {
            const title = (group.title || group.localizedTitle || '').toLowerCase();
            return names.some(n => title.includes(n.toLowerCase()));
          });
        };

        const findStatInGroup = (group, possibleStatNames) => {
          if (!group?.stats) return null;
          const names = Array.isArray(possibleStatNames) ? possibleStatNames : [possibleStatNames];
          return group.stats.find(s => {
            const t = (s.title || s.localizedTitle || '').toLowerCase();
            return names.some(n => t.includes(n.toLowerCase()));
          });
        };

        const getValues = (statObj) => {
          if (!statObj?.stats || statObj.stats.length < 2) return { home: 0, away: 0 };
          return {
            home: Number(statObj.stats[0]) || 0,
            away: Number(statObj.stats[1]) || 0
          };
        };

        const topStatsGroup = findStatGroup(['Top stats', 'top statistics', 'general']);
        const shotsGroup    = findStatGroup(['Shots', 'shooting']);
        const disciplineGroup = findStatGroup(['Discipline', 'Cards', 'Disciplinary']);

        const possession = getValues(findStatInGroup(topStatsGroup, ['Ball possession', 'Possession']));
        
        let shots = getValues(findStatInGroup(shotsGroup, ['Total shots', 'Shots total', 'total shots', 'Shots']));

        if (shots.home === 0 && shots.away === 0 && shotsGroup?.stats?.length > 0) {
          const totalShotsStat = shotsGroup.stats.find(s => 
            (s.key === 'total_shots') || 
            (s.title?.toLowerCase().includes('total shots'))
          );

          if (totalShotsStat?.stats?.length >= 2) {
            shots = {
              home: Number(totalShotsStat.stats[0]) || 0,
              away: Number(totalShotsStat.stats[1]) || 0
            };
          }
        }

        if (shots.home === 0 && shots.away === 0 && shotsGroup?.stats?.length >= 2) {
          const secondStat = shotsGroup.stats[1];
          if (secondStat?.stats?.length >= 2 && secondStat.type !== 'title') {
            shots = {
              home: Number(secondStat.stats[0]) || 0,
              away: Number(secondStat.stats[1]) || 0
            };
          }
        }

        const shotsOn    = getValues(findStatInGroup(shotsGroup, ['Shots on target', 'Shots on goal', 'On target']));
        const corners    = getValues(findStatInGroup(topStatsGroup || shotsGroup, ['Corners']));
        const fouls      = getValues(findStatInGroup(topStatsGroup, ['Fouls committed', 'Fouls']));

        let yellowCards = getValues(findStatInGroup(disciplineGroup, ['Yellow cards', 'Yellow']));
        let redCards    = getValues(findStatInGroup(disciplineGroup, ['Red cards', 'Red']));

        if (yellowCards.home === 0 && yellowCards.away === 0) {
          yellowCards = getValues(findStatInGroup(topStatsGroup, ['Yellow cards']));
        }
        if (redCards.home === 0 && redCards.away === 0) {
          redCards = getValues(findStatInGroup(topStatsGroup, ['Red cards']));
        }

        setStats({
          possession,
          shots,
          shotsOn,
          corners,
          fouls,
          yellowCards,
          redCards,
        });

      } catch (err) {
        console.error('Match fetch error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMatch();
  }, [id]);

  const formatFullDate = (iso) => {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  // MPEG-TS Player Setup
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const selected = streamSources[activeTab];
    if (!selected || selected.type !== 'mpegts') {
      setStreamStatus('idle');
      return;
    }

    setStreamStatus('loading');

    if (mpegts.isSupported()) {
      const player = mpegts.createPlayer({
        type: 'mpegts',
        url: selected.src,
        isLive: true,
      }, {
        enableWorker: true,
        stashInitialSize: 1024 * 1024 * 30, // 30MB for better stability with unstable streams
        enableStashBuffer: true,
        autoCleanupSourceBuffer: true,
        autoCleanupMaxBackwardDuration: 90,
        autoCleanupMinBackwardDuration: 30,
      });

      playerRef.current = player;

      player.attachMediaElement(video);
      player.load();

      // Events handling
      player.on(mpegts.Events.LOADING_COMPLETE, () => {
        setStreamStatus('playing');
        video.play().catch(() => setStreamStatus('needs-interaction'));
      });

      player.on(mpegts.Events.ERROR, (errType, errDetail) => {
        console.error('mpegts error:', errType, errDetail);
        setStreamStatus('error');
      });

      player.on(mpegts.Events.BUFFERING, () => setStreamStatus('buffering'));
      player.on(mpegts.Events.RECOVERED, () => setStreamStatus('playing'));
      player.on(mpegts.Events.MEDIA_INFO, () => {
        if (player.mediaInfo?.isLive) {
          setStreamStatus('playing');
        }
      });

      return () => {
        if (playerRef.current) {
          playerRef.current.destroy();
          playerRef.current = null;
        }
      };
    } else {
      setStreamStatus('error');
      console.warn('Browser does not support MSE for mpegts.js');
    }
  }, [activeTab]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 to-black">
      <div className="text-emerald-400 text-xl animate-pulse">Loading match details...</div>
    </div>
  );

  if (error || !match) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 to-black">
      <div className="text-center px-6 py-12 max-w-md">
        <h2 className="text-2xl text-white mb-4">Match not available</h2>
        <p className="text-gray-400">Data is temporarily unavailable or match doesn't exist</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-black to-gray-950 text-white pb-16 lg:pb-24">
      <div className="container mx-auto px-4 sm:px-6 pt-8 md:pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">

          {/* Main Content */}
          <div className="lg:col-span-8 xl:col-span-9 space-y-6 lg:space-y-8">
            {/* Match Card */}
            <div className={`bg-gray-900/70 backdrop-blur-md border border-gray-800 rounded-2xl overflow-hidden shadow-2xl
              ${match.status === 'live' ? 'border-red-600/50 shadow-red-900/30 ring-1 ring-red-600/30' : ''}`}>
              <div className="p-5 sm:p-7 lg:p-8">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="inline-block px-4 py-1 bg-gray-800/80 rounded-full text-sm text-gray-300 mb-3">
                    {match.group}
                  </div>
                  {match.status === 'live' && (
                    <div className="inline-flex items-center gap-2 bg-red-600/90 px-5 py-2 rounded-full text-sm font-bold animate-pulse">
                      <span className="w-3 h-3 bg-white rounded-full"></span>
                      LIVE • {match.minute || "?"}'
                    </div>
                  )}
                </div>

                {/* Teams */}
                <div className="grid grid-cols-3 items-center gap-4 sm:gap-6 md:gap-8 mb-8">
                  {/* Home team */}
                  <div className="text-center">
                    <img
                      src={match.homeBadge}
                      alt={match.homeTeam}
                      className="w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 lg:w-40 lg:h-40 mx-auto object-contain mb-3 md:mb-4"
                      onError={e => e.target.src = fallbackBadge}
                    />
                    <h2 className="text-base sm:text-xl md:text-2xl font-bold leading-tight">
                      {match.homeTeam}
                    </h2>
                  </div>

                  {/* Center - Score / VS / Penalties */}
                  <div className="text-center">
                    {match.status === 'upcoming' ? (
                      <div className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-500 select-none">VS</div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 md:gap-2">
                        <div
                          className="
                            text-4xl xs:text-5xl sm:text-6xl md:text-6xl lg:text-7xl 
                            font-black text-emerald-400 tracking-[-0.02em]
                            whitespace-nowrap flex items-center justify-center gap-2 sm:gap-3
                          "
                        >
                          <span>{match.homeScore ?? '-'}</span>
                          <span className="text-gray-500 opacity-70 mx-1 sm:mx-2">–</span>
                          <span>{match.awayScore ?? '-'}</span>
                        </div>

                        {match.status === 'finished' && match.homeScore === match.awayScore && (
                          <div className="text-lg sm:text-xl md:text-2xl font-bold text-yellow-400 animate-fade-in">
                            {penaltyInfo ? (
                              penaltyInfo.aggregate ? (
                                <span>{penaltyInfo.aggregate}</span>  // "Pen 2 - 4" or similar
                              ) : (
                                <>
                                  {penaltyInfo.homePens}–{penaltyInfo.awayPens} pens
                                  <br className="sm:hidden" />
                                  <span className="text-base sm:text-lg">
                                    {penaltyInfo.winner} wins! 🏆
                                  </span>
                                </>
                              )
                            ) : (
                              <span className="text-yellow-600/80 animate-pulse text-base sm:text-lg">
                                Checking shootout...
                              </span>
                            )}
                          </div>
                        )}

                        {match.status === 'finished' && (
                          <div className="text-xs sm:text-sm md:text-base text-gray-400 mt-1 md:mt-2 font-medium">
                            Full Time {penaltyInfo ? '(Pens)' : ''}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Away team */}
                  <div className="text-center">
                    <img
                      src={match.awayBadge}
                      alt={match.awayTeam}
                      className="w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 lg:w-40 lg:h-40 mx-auto object-contain mb-3 md:mb-4"
                      onError={e => e.target.src = fallbackBadge}
                    />
                    <h2 className="text-base sm:text-xl md:text-2xl font-bold leading-tight">
                      {match.awayTeam}
                    </h2>
                  </div>
                </div>

                {/* Match Info */}
                <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-gray-400 text-sm sm:text-base">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{formatFullDate(match.kickoffTime)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    <span>{match.stadium} • {match.city}</span>
                  </div>
                </div>
              </div>

              {/* Live Stream Section */}
              {isLiveOrUpcoming && streamSources.length > 0 && (
                <div className="mt-6 lg:mt-8 border-t border-gray-800 pt-6 lg:pt-8 px-5 sm:px-7 lg:px-8 pb-8">
                  <h3 className="text-xl sm:text-2xl font-bold mb-5 flex items-center gap-3">
                    <Tv className="text-emerald-400" />
                    Live Streams
                  </h3>

                  {/* Quality Tabs */}
                  <div className="flex flex-wrap gap-2 mb-5 border-b border-gray-800 pb-3">
                    {streamSources.map((stream, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveTab(idx)}
                        className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                          activeTab === idx
                            ? 'bg-emerald-600/80 text-white shadow-md'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {stream.title}
                      </button>
                    ))}
                  </div>

                  {/* Video Player */}
                  <div className="aspect-video bg-black rounded-xl overflow-hidden border border-gray-800 shadow-2xl relative">
                    {streamSources[activeTab]?.type === 'iframe' ? (
                      <iframe
                        src={streamSources[activeTab].src}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        title={streamSources[activeTab].title}
                        className="w-full h-full"
                      />
                    ) : (
                      <>
                        <video
                          ref={videoRef}
                          controls
                          autoPlay
                          muted
                          className="w-full h-full"
                          playsInline
                        />

                        {streamStatus !== 'playing' && streamStatus !== 'idle' && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white z-10">
                            <div className="text-center p-6 max-w-md">
                              {streamStatus === 'loading' && (
                                <p className="text-xl animate-pulse">Loading stream... (may take 30–120s)</p>
                              )}
                              {streamStatus === 'buffering' && (
                                <p className="text-xl">Buffering... please wait</p>
                              )}
                              {streamStatus === 'needs-interaction' && (
                                <p className="text-xl">Click play to start</p>
                              )}
                              {streamStatus === 'error' && (
                                <div>
                                  <p className="text-xl text-red-400 mb-3">Failed to load stream</p>
                                  <p className="text-sm">Source may be offline, expired, or restricted. Try another quality.</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mt-4 text-center">
                    Switch quality if stream doesn't load properly (Telegram sources can be unstable)
                  </p>
                </div>
              )}
            </div>
            <TinyAd />

            {/* Statistics Section */}
            {stats && (
              <div className="bg-gray-900/70 backdrop-blur-md border border-gray-800 rounded-2xl p-6 lg:p-8">
                <h3 className="text-xl sm:text-2xl font-bold mb-6 flex items-center gap-3">
                  <BarChart2 className="text-emerald-400" />
                  Match Statistics
                </h3>

                <div className="space-y-5">
                  {stats.possession.home + stats.possession.away > 0 && (
                    <StatBar label="Possession %" home={stats.possession.home} away={stats.possession.away} />
                  )}
                  <StatBar label="Shots" home={stats.shots.home} away={stats.shots.away} />
                  <StatBar label="Shots on Target" home={stats.shotsOn.home} away={stats.shotsOn.away} />
                  <StatBar label="Corners" home={stats.corners.home} away={stats.corners.away} />
                  <StatBar label="Fouls" home={stats.fouls.home} away={stats.fouls.away} />
                </div>

                {stats && (stats.yellowCards.home + stats.yellowCards.away + 
                          stats.redCards.home + stats.redCards.away > 0) && (
                  <div className="mt-10 pt-8 border-t border-gray-800">
                    <h4 className="text-lg font-semibold mb-6 text-center flex items-center justify-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      Discipline & Cards
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 md:gap-16 max-w-4xl mx-auto">
                      <div className="space-y-8">
                        <div className="flex flex-col items-center bg-gray-950/40 rounded-xl p-6 border border-gray-800/50">
                          <div className="flex items-center gap-4 mb-4">
                            <img
                              src={match.homeBadge}
                              alt={match.homeTeam}
                              className="w-14 h-14 object-contain rounded-full border-2 border-gray-700 shadow-md"
                              onError={e => e.target.src = 'https://via.placeholder.com/56?text=T'}
                            />
                            <h5 className="text-xl font-bold text-white">{match.homeTeam}</h5>
                          </div>

                          <div className="w-full space-y-6">
                            <div className="text-center">
                              <div className="text-6xl font-black text-yellow-400 drop-shadow-lg">
                                {stats.yellowCards.home}
                              </div>
                              <div className="text-sm uppercase tracking-wide text-yellow-500/80 mt-1">Yellow Cards</div>
                            </div>

                            <div className="text-center">
                              <div className="text-6xl font-black text-red-500 drop-shadow-lg">
                                {stats.redCards.home}
                              </div>
                              <div className="text-sm uppercase tracking-wide text-red-500/80 mt-1">Red Cards</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-8">
                        <div className="flex flex-col items-center bg-gray-950/40 rounded-xl p-6 border border-gray-800/50">
                          <div className="flex items-center gap-4 mb-4">
                            <img
                              src={match.awayBadge}
                              alt={match.awayTeam}
                              className="w-14 h-14 object-contain rounded-full border-2 border-gray-700 shadow-md"
                              onError={e => e.target.src = 'https://via.placeholder.com/56?text=T'}
                            />
                            <h5 className="text-xl font-bold text-white">{match.awayTeam}</h5>
                          </div>

                          <div className="w-full space-y-6">
                            <div className="text-center">
                              <div className="text-6xl font-black text-yellow-400 drop-shadow-lg">
                                {stats.yellowCards.away}
                              </div>
                              <div className="text-sm uppercase tracking-wide text-yellow-500/80 mt-1">Yellow Cards</div>
                            </div>

                            <div className="text-center">
                              <div className="text-6xl font-black text-red-500 drop-shadow-lg">
                                {stats.redCards.away}
                              </div>
                              <div className="text-sm uppercase tracking-wide text-red-500/80 mt-1">Red Cards</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Official Broadcasters */}
            <div className="bg-gray-900/60 rounded-2xl p-6 lg:p-8 border border-gray-800">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                <Tv className="text-emerald-400" />
                Official Broadcasters
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <BroadcasterButton name="CAF TV" icon={Youtube} url="https://www.youtube.com/@CAF_TV" color="bg-red-900/40" />
                <BroadcasterButton name="beIN Sports" icon={Tv} url="https://www.bein.com/en/afcon25/" color="bg-blue-900/40" />
                <BroadcasterButton name="SuperSport" icon={Tv} url="https://www.supersport.com" color="bg-purple-900/40" />
              </div>
              <p className="text-xs text-gray-500 mt-5 text-center">
                Legal streams • Availability depends on your region
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 xl:col-span-3 space-y-6">
            <div className="bg-gray-900/70 backdrop-blur-md border border-gray-800 rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Shield className="text-emerald-400" />
                Match Information
              </h3>
              <div className="space-y-4 text-sm text-gray-300">
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className="font-medium">
                    {match.status === 'live' ? 'In Progress' : 
                     match.status === 'finished' ? 'Finished' : 'Upcoming'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Kick-off</span>
                  <span className="font-medium">{formatFullDate(match.kickoffTime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Venue</span>
                  <span className="font-medium text-right">
                    {match.stadium}<br />
                    <span className="text-gray-500 text-sm">{match.city}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      <TinyAd />
    </div>
  );
};